import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, alpha, Divider, Skeleton, Tooltip, IconButton
} from '@mui/material';
import {
  Upload as UploadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalance as AccountBalanceIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  ReportProblem as ReportProblemIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import * as api from '../../api/api';


// Month abbreviations for table headers
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

function ForecastVsActualsStep({ 
  outcomeId,
  data,
  outcomeName,
  onUpdate,
  onSave,
  buttonStyle,
  secondaryButtonStyle,
  autoSave = false,
  hideButtons = false,
  readOnly = false
}) {
  const [outcome, setOutcome] = useState(null);
  const [staffingData, setStaffingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDeptFilter, setSelectedDeptFilter] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [expandedCapex, setExpandedCapex] = useState(new Set());
    // Upload functionality removed for wizard step
  const [year] = useState(new Date().getFullYear());

  const toggleCapex = useCallback((rowKey) => {
    setExpandedCapex(prev => {
      const next = new Set(prev);
      next.has(rowKey) ? next.delete(rowKey) : next.add(rowKey);
      return next;
    });
  }, []);
  
  // Helper functions for latest month calculations
  const getLatestActualSpendMonth = useCallback((monthlyActual) => {
    let latestMonth = -1;
    for (let i = 11; i >= 0; i--) {
      if (monthlyActual[i] !== null && monthlyActual[i] > 0) {
        latestMonth = i;
        break;
      }
    }
    return latestMonth;
  }, []);

  const getCumulativeForecastThroughMonth = useCallback((monthlyForecast, throughMonth) => {
    if (throughMonth < 0) return 0;
    return monthlyForecast.slice(0, throughMonth + 1).reduce((sum, amount) => sum + (amount || 0), 0);
  }, []);
    

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatCurrencyFull = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

    // Helper function to format percentage
  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

            // Helper function to calculate company breakdown from staffing data with monthly details
  const calculateCompanyBreakdown = useCallback((staffingData) => {
    const companyBreakdown = {};
    const fixedCostBreakdown = {};
    
    // Helper to initialize company structure
    const initializeCompany = (company) => {
      if (!companyBreakdown[company]) {
        companyBreakdown[company] = {
          monthlyForecast: Array(12).fill(0),
          monthlyCapex: Array(12).fill(0),
          monthlyOpex: Array(12).fill(0)
        };
      }
    };
    
    // Helper to initialize fixed cost structure
    const initializeFixedCost = (costName) => {
      if (!fixedCostBreakdown[costName]) {
        fixedCostBreakdown[costName] = {
          monthlyForecast: Array(12).fill(0),
          monthlyCapex: Array(12).fill(0),
          monthlyOpex: Array(12).fill(0)
        };
      }
    };
    
    if (process.env.NODE_ENV === 'development' && staffingData.milestones?.length > 10) {
      console.log('calculateCompanyBreakdown: Processing large dataset with', staffingData.milestones?.length || 0, 'milestones');
    }
    
    if (staffingData.milestones) {
      staffingData.milestones.forEach((milestone, milestoneIndex) => {
        if (milestone.roles) {
          milestone.roles.forEach((role, roleIndex) => {
            const company = role.company || 'Unknown';
            initializeCompany(company);
            
                        const monthlyRate = role.rate || 0;
            const baseAllocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
            
            if (process.env.NODE_ENV === 'development' && milestoneIndex === 0 && milestone.roles.length > 5) {
              console.log(`Processing ${staffingData.milestones.length} milestones with ${milestone.roles.length}+ roles each`);
            }
            
            // Process dateRanges with dual format support
            if (role.dateRanges && role.dateRanges.length > 0) {
              role.dateRanges.forEach((range, rangeIndex) => {
                let startMonth, endMonth;
                
                const isCalendarFormat = (
                  range.timelineVersion === '3.0' ||
                  (range.startMonth >= 1 && range.startMonth <= 12 && 
                   range.endMonth >= 1 && range.endMonth <= 12 &&
                   range.startMonth <= range.endMonth && 
                   !range.useProjectRelative)
                );
                
                if (isCalendarFormat) {
                  startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
                  endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
                                } else {
                  startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
                  endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
                  
                  if (process.env.NODE_ENV === 'development' && !isCalendarFormat) {
                    console.log(`ForecastVsActualsStep: Treating range ${range.startMonth}-${range.endMonth} as calendar months`);
                  }
                }
                
                const rangeAllocation = Math.max(0, Math.min(100, range.allocation || 0)) / 100;
                
                for (let month = startMonth; month <= endMonth; month++) {
                  const monthIndex = month - 1;
                  if (monthIndex >= 0 && monthIndex < 12) {
                    const monthlyCost = monthlyRate * rangeAllocation;
                    
                    if (!isNaN(monthlyCost) && monthlyCost >= 0) {
                      companyBreakdown[company].monthlyForecast[monthIndex] += monthlyCost;
                      
                      if (role.capex_classification === 'CAPEX') {
                        companyBreakdown[company].monthlyCapex[monthIndex] += monthlyCost;
                      } else {
                        companyBreakdown[company].monthlyOpex[monthIndex] += monthlyCost;
                      }
                    }
                  }
                }
              });
                        } else {
              if (process.env.NODE_ENV === 'development') {
                console.warn(`Role ${role.name} has no dateRanges - defaulting to milestone dates`);
              }
              
              const startMonth = role.milestone_start_month || 1;
              const endMonth = role.milestone_end_month || 12;
              const allocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
              
              for (let month = startMonth; month <= endMonth; month++) {
                const monthIndex = month - 1;
                if (monthIndex >= 0 && monthIndex < 12) {
                  const monthlyCost = monthlyRate * allocation;
                  
                  if (!isNaN(monthlyCost) && monthlyCost >= 0) {
                    companyBreakdown[company].monthlyForecast[monthIndex] += monthlyCost;
                    
                    if (role.capex_classification === 'CAPEX') {
                      companyBreakdown[company].monthlyCapex[monthIndex] += monthlyCost;
                    } else {
                      companyBreakdown[company].monthlyOpex[monthIndex] += monthlyCost;
                    }
                  }
                }
              }
            }
          });
        }
      });
    }
    
                // Add fixed costs to company breakdown AND track separately for detailed reporting
    if (staffingData.fixedCosts) {
      staffingData.fixedCosts.forEach((cost, costIndex) => {
        const costName = cost.type || cost.name || `Fixed Cost ${costIndex + 1}`;
        const company = cost.company || 'Other';
        
        initializeCompany(company);
        initializeFixedCost(costName);
        
        // Store the details and company info immediately
        fixedCostBreakdown[costName].details = cost.details || '';
        fixedCostBreakdown[costName].company = company;
        
        let startDate, endDate;
        try {
          startDate = dayjs(cost.start_date + '-01');
          endDate = dayjs(cost.end_date + '-01');
          
          if (!startDate.isValid() || !endDate.isValid()) {
            console.warn(`Invalid dates for fixed cost ${costIndex}:`, cost.start_date, cost.end_date);
            return;
          }
        } catch (error) {
          console.warn(`Error parsing dates for fixed cost ${costIndex}:`, error);
          return;
        }
        
                const monthlyCost = cost.monthlyCost || cost.monthly_cost || 0;
        const startMonth = Math.max(1, Math.min(12, startDate.month() + 1));
        const endMonth = Math.max(startMonth, Math.min(12, endDate.month() + 1));
        
        if (process.env.NODE_ENV === 'development' && costIndex === 0 && staffingData.fixedCosts.length > 5) {
          console.log(`Processing ${staffingData.fixedCosts.length} fixed costs`);
        }
        
        if (!isNaN(monthlyCost) && monthlyCost >= 0) {
          for (let month = startMonth; month <= endMonth; month++) {
            const monthIndex = month - 1;
            if (monthIndex >= 0 && monthIndex < 12) {
              // Add to company breakdown (this will be used for totals)
              companyBreakdown[company].monthlyForecast[monthIndex] += monthlyCost;
              
              // Also track in fixed cost breakdown for detailed reporting
              fixedCostBreakdown[costName].monthlyForecast[monthIndex] += monthlyCost;
              
              if ((cost.capex_classification || 'OPEX') === 'CAPEX') {
                companyBreakdown[company].monthlyCapex[monthIndex] += monthlyCost;
                fixedCostBreakdown[costName].monthlyCapex[monthIndex] += monthlyCost;
              } else {
                companyBreakdown[company].monthlyOpex[monthIndex] += monthlyCost;
                fixedCostBreakdown[costName].monthlyOpex[monthIndex] += monthlyCost;
              }
            }
          }
        }
      });
    }
    
    // Calculate totals from monthly arrays
    Object.keys(companyBreakdown).forEach(company => {
      const companyData = companyBreakdown[company];
      companyData.capex = companyData.monthlyCapex.reduce((sum, val) => sum + val, 0);
      companyData.opex = companyData.monthlyOpex.reduce((sum, val) => sum + val, 0);
      companyData.total = companyData.monthlyForecast.reduce((sum, val) => sum + val, 0);
    });
    
    Object.keys(fixedCostBreakdown).forEach(costName => {
      const costData = fixedCostBreakdown[costName];
      costData.capex = costData.monthlyCapex.reduce((sum, val) => sum + val, 0);
      costData.opex = costData.monthlyOpex.reduce((sum, val) => sum + val, 0);
      costData.total = costData.monthlyForecast.reduce((sum, val) => sum + val, 0);
      
              // Ensure details and company are preserved from the original data
      if (!costData.details && !costData.company) {
        // Try to find the original cost data to get details
        const originalCost = staffingData.fixedCosts?.find(cost => 
          (cost.type || cost.name || `Fixed Cost ${staffingData.fixedCosts.indexOf(cost) + 1}`) === costName
        );
        if (originalCost) {
          costData.details = originalCost.details || '';
          costData.company = originalCost.company || 'Other';
        }
      }
        });
    
    if (process.env.NODE_ENV === 'development' && Object.keys(companyBreakdown).length > 3) {
      console.log('Company breakdown completed:', Object.keys(companyBreakdown).length, 'companies', Object.keys(fixedCostBreakdown).length, 'fixed costs');
    }
    
    return { companyBreakdown, fixedCostBreakdown };
  }, []);

      // Helper function to distribute actual spend by company based on forecast proportions
  const distributeActualSpendByCompany = useCallback((companyBreakdown, actualSpendData, totalForecast) => {
    const companyActuals = {};
    
    const monthlyActual = Array(12).fill(null);
    if (actualSpendData.monthlySpend) {
      Object.entries(actualSpendData.monthlySpend).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyActual[monthIndex] = amount;
        }
      });
    }
    
    const totalActualSpend = (actualSpendData.hasUploadedData && actualSpendData.totalActual !== undefined) ? 
      actualSpendData.totalActual : 
      monthlyActual.filter(amount => amount !== null).reduce((sum, amount) => sum + amount, 0);
    
    if (actualSpendData.hasUploadedData && actualSpendData.companySpend) {
      Object.entries(actualSpendData.companySpend).forEach(([company, spendData]) => {
        companyActuals[company] = {
          capex: spendData.capex || 0,
          opex: spendData.opex || 0,
          total: spendData.total || ((spendData.capex || 0) + (spendData.opex || 0))
        };
      });
    } else if (totalActualSpend > 0 && totalForecast > 0) {
      Object.entries(companyBreakdown).forEach(([company, forecast]) => {
        const companyProportion = forecast.total / totalForecast;
        const companyActualTotal = totalActualSpend * companyProportion;
        
        const capexProportion = forecast.total > 0 ? forecast.capex / forecast.total : 0;
        const opexProportion = forecast.total > 0 ? forecast.opex / forecast.total : 1;
        
        companyActuals[company] = {
          capex: companyActualTotal * capexProportion,
          opex: companyActualTotal * opexProportion,
          total: companyActualTotal
        };
      });
    }
    
    return companyActuals;
  }, []);

    

      

            // Get actual spend data by month
  const getActualSpendData = useCallback(() => {
    const monthlyActual = Array(12).fill(null);
    
    if (outcome?.actualSpendData?.monthlySpend) {
      Object.entries(outcome.actualSpendData.monthlySpend).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyActual[monthIndex] = amount;
        }
      });
    }
    
    const totalActualSpend = monthlyActual
      .filter(amount => amount !== null)
      .reduce((sum, amount) => sum + amount, 0);

        return {
      monthlyActual,
      totalActualSpend
    };
  }, [outcome]);

  // Get official forecast data by month
  const getOfficialForecastData = useCallback(() => {
    const monthlyOfficialForecast = Array(12).fill(null);
    const monthlyOfficialCapex = Array(12).fill(null);
    const monthlyOfficialOpex = Array(12).fill(null);
    
    if (outcome?.officialForecastData?.monthlyForecast) {
      Object.entries(outcome.officialForecastData.monthlyForecast).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyOfficialForecast[monthIndex] = amount;
        }
      });
    }
    
    if (outcome?.officialForecastData?.monthlyCapex) {
      Object.entries(outcome.officialForecastData.monthlyCapex).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyOfficialCapex[monthIndex] = amount;
        }
      });
    }
    
    if (outcome?.officialForecastData?.monthlyOpex) {
      Object.entries(outcome.officialForecastData.monthlyOpex).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyOfficialOpex[monthIndex] = amount;
        }
      });
    }
    
    const totalOfficialForecast = monthlyOfficialForecast
      .filter(amount => amount !== null)
      .reduce((sum, amount) => sum + amount, 0);
      
    const totalOfficialCapex = monthlyOfficialCapex.filter(amount => amount !== null).reduce((sum, amount) => sum + amount, 0)
      || (outcome?.officialForecastData?.totalCapex || 0);

    const totalOfficialOpex = monthlyOfficialOpex.filter(amount => amount !== null).reduce((sum, amount) => sum + amount, 0)
      || (outcome?.officialForecastData?.totalOpex || 0);

    return {
      monthlyOfficialForecast,
      monthlyOfficialCapex,
      monthlyOfficialOpex,
      totalOfficialForecast,
      totalOfficialCapex,
      totalOfficialOpex,
      hasOfficialForecast: totalOfficialForecast > 0
    };
  }, [outcome]);

        // Calculate outcome totals from staffing data using company breakdown as single source of truth
  const calculateOutcomeTotalsFromStaffing = useCallback((overrideData = null) => {
    const dataToUse = overrideData || staffingData;
    if (!dataToUse?.milestones) {
      return {
        monthlyProjected: Array(12).fill(0),
        monthlyCapex: Array(12).fill(0),
        monthlyOpex: Array(12).fill(0),
        totalProjected: 0,
        totalCapex: 0,
        totalOpex: 0,
        companyBreakdown: {},
        companyActuals: {},
        companyMonthlyActuals: {},
        fixedCostBreakdown: {},
        fixedCostActuals: {}
      };
    }

    const { companyBreakdown, fixedCostBreakdown } = calculateCompanyBreakdown(dataToUse);

    // When an official forecast exists, replace CA staffing costs month-by-month with the
    // official forecast amounts. CA fixed costs are added on top. All other vendors keep
    // their staffing model amounts. When no official forecast exists, all companies use staffing.
    const officialForecastData = outcome?.officialForecastData;
    const hasOfficialForecast = !!(officialForecastData &&
      (officialForecastData.hasUploadedForecastData ||
       officialForecastData.fromUpload ||
       officialForecastData.uploadedForecast ||
       officialForecastData.hasOfficialForecast));

    // Pre-mutation snapshot of staffing costs per vendor — populated when official forecast exists.
    // Used to show pure PPM staffing separately from the official forecast.
    const companyStaffingBreakdown = {};

    // Track pure staffing totals for PPM comparison
    let monthlyStaffingProjected = Array(12).fill(0);

    const vendorMonthlyForecast = officialForecastData?.companyMonthlyForecast || {};
    const hasAnyVendorForecast = hasOfficialForecast && Object.keys(vendorMonthlyForecast).length > 0;
    const caMonthlySource = vendorMonthlyForecast['CA'] || (!hasAnyVendorForecast && officialForecastData?.monthlyForecast);

    if (hasOfficialForecast && (hasAnyVendorForecast || caMonthlySource)) {
      // Snapshot ALL companies and compute pure-staffing totals BEFORE any mutation
      Object.entries(companyBreakdown).forEach(([company, companyData]) => {
        for (let i = 0; i < 12; i++) {
          monthlyStaffingProjected[i] += companyData.monthlyForecast[i] || 0;
        }
        companyStaffingBreakdown[company] = {
          total: companyData.total,
          capex: companyData.capex,
          opex: companyData.opex,
          monthlyForecast: [...(companyData.monthlyForecast || Array(12).fill(0))],
        };
      });

      // Determine which vendors to replace
      const vendorsToReplace = hasAnyVendorForecast ? Object.keys(vendorMonthlyForecast) : ['CA'];
      const monthlySources = hasAnyVendorForecast ? vendorMonthlyForecast : { CA: caMonthlySource };

      vendorsToReplace.forEach(vendor => {
        const monthlySource = monthlySources[vendor];
        if (!monthlySource) return;

        // Sum fixed costs attributed to this vendor
        const vFixedMonthly = Array(12).fill(0);
        const vFixedCapex = Array(12).fill(0);
        const vFixedOpex = Array(12).fill(0);
        Object.values(fixedCostBreakdown).forEach(fc => {
          if ((fc.company || '').toUpperCase() === vendor.toUpperCase()) {
            for (let i = 0; i < 12; i++) {
              vFixedMonthly[i] += fc.monthlyForecast[i] || 0;
              vFixedCapex[i] += fc.monthlyCapex[i] || 0;
              vFixedOpex[i] += fc.monthlyOpex[i] || 0;
            }
          }
        });

        // Build official + fixed monthly arrays (1-indexed keys → 0-indexed)
        const vOfficialMonthly = Array(12).fill(0);
        const vOfficialCapex = Array(12).fill(0);
        const vOfficialOpex = Array(12).fill(0);
        Object.entries(monthlySource).forEach(([month, amount]) => {
          const i = parseInt(month) - 1;
          if (i >= 0 && i < 12) {
            vOfficialMonthly[i] = (amount || 0) + vFixedMonthly[i];
            vOfficialCapex[i] = vFixedCapex[i];
            vOfficialOpex[i] = (amount || 0) + vFixedOpex[i];
          }
        });

        companyBreakdown[vendor] = {
          ...(companyBreakdown[vendor] || {}),
          monthlyForecast: vOfficialMonthly,
          monthlyCapex: vOfficialCapex,
          monthlyOpex: vOfficialOpex,
          total: vOfficialMonthly.reduce((s, v) => s + v, 0),
          capex: vOfficialCapex.reduce((s, v) => s + v, 0),
          opex: vOfficialOpex.reduce((s, v) => s + v, 0),
        };
      });
    }

    let monthlyProjected = Array(12).fill(0);
    let monthlyCapex = Array(12).fill(0);
    let monthlyOpex = Array(12).fill(0);
    let totalProjected = 0;
    let totalCapex = 0;
    let totalOpex = 0;

    if (Object.keys(companyBreakdown).length > 0 || Object.keys(fixedCostBreakdown).length > 0) {
      Object.values(companyBreakdown).forEach(company => {
        for (let i = 0; i < 12; i++) {
          monthlyProjected[i] += company.monthlyForecast[i] || 0;
          monthlyCapex[i] += company.monthlyCapex[i] || 0;
          monthlyOpex[i] += company.monthlyOpex[i] || 0;
        }
        totalProjected += company.total || 0;
        totalCapex += company.capex || 0;
        totalOpex += company.opex || 0;
      });
      // Fixed costs are already included in companyBreakdown above
    }

    // If no official forecast was applied, staffing IS the projected
    if (monthlyStaffingProjected.every(v => v === 0)) {
      monthlyStaffingProjected = [...monthlyProjected];
    }

    // Get actual spend data and company distribution
    let companyActuals = {};
    let companyMonthlyActuals = {};
    
    if (outcome?.actualSpendData?.hasUploadedData && outcome?.actualSpendData?.companySpend) {
      Object.entries(outcome.actualSpendData.companySpend).forEach(([company, spendData]) => {
        companyActuals[company] = {
          capex: spendData.capex || 0,
          opex: spendData.opex || 0,
          total: spendData.total || ((spendData.capex || 0) + (spendData.opex || 0))
        };
      });
      
      if (outcome.actualSpendData.companyMonthlySpend) {
        Object.entries(outcome.actualSpendData.companyMonthlySpend).forEach(([company, monthlyData]) => {
          companyMonthlyActuals[company] = Array(12).fill(0);
          if (monthlyData && typeof monthlyData === 'object') {
            Object.entries(monthlyData).forEach(([month, amount]) => {
              const monthIndex = parseInt(month) - 1;
              if (monthIndex >= 0 && monthIndex < 12) {
                companyMonthlyActuals[company][monthIndex] = amount || 0;
              }
            });
          }
        });
      }
    }
    
    // Fixed cost actuals - ONLY from uploaded data
    const fixedCostActuals = {};
    if (outcome?.actualSpendData?.hasUploadedData && outcome?.actualSpendData?.fixedCostSpend) {
      Object.entries(outcome.actualSpendData.fixedCostSpend).forEach(([costName, actualData]) => {
        fixedCostActuals[costName] = {
          capex: actualData.capex || 0,
          opex: actualData.opex || 0,
          total: actualData.total || ((actualData.capex || 0) + (actualData.opex || 0))
        };
      });
    }

    return {
      monthlyProjected,
      monthlyCapex,
      monthlyOpex,
      monthlyStaffingProjected,
      totalProjected,
      totalCapex,
      totalOpex,
      companyBreakdown,
      companyActuals,
      companyMonthlyActuals,
      fixedCostBreakdown,
      fixedCostActuals,
      companyStaffingBreakdown,
    };
  }, [staffingData, calculateCompanyBreakdown, outcome]);

      const calculateSpendStatus = useCallback((actualTotal, baselineForecast, monthlyActual, monthlyBaseline, approvedBudget, totalProjected) => {
    const latestActualSpendMonth = getLatestActualSpendMonth(monthlyActual);

    let actualSpendThroughLatestMonth = 0;
    let baselineForecastThroughActualMonth = 0;

    if (latestActualSpendMonth >= 0) {
      actualSpendThroughLatestMonth = (monthlyActual || [])
        .slice(0, latestActualSpendMonth + 1)
        .reduce((sum, v) => sum + (v || 0), 0);
      baselineForecastThroughActualMonth = getCumulativeForecastThroughMonth(
        monthlyBaseline || [],
        latestActualSpendMonth
      );
    }

    const projectedOverBudget = approvedBudget > 0 && totalProjected > approvedBudget;
    const ytdActualOverProjected = actualSpendThroughLatestMonth > 0 &&
      baselineForecastThroughActualMonth > 0 &&
      actualSpendThroughLatestMonth > baselineForecastThroughActualMonth;

    if (projectedOverBudget && ytdActualOverProjected) return { status: 'needs-attention', text: 'Needs Attention' };
    if (!projectedOverBudget && ytdActualOverProjected) return { status: 'at-risk',         text: 'At Risk' };
    if (projectedOverBudget)                            return { status: 'off-track',       text: 'Off Track' };
    return { status: 'on-track', text: 'On Track' };
  }, [getLatestActualSpendMonth, getCumulativeForecastThroughMonth]);

    

                                  

  
  
  // Fetch outcome data and calculate projections
  useEffect(() => {
    const fetchOutcomeData = async () => {
      if (!outcomeId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch outcome basic info
        console.log('ForecastVsActualsStep: Fetching outcome data...');
        const outcomeStartTime = performance.now();
        const outcomeResponse = await api.getOutcome(outcomeId);
        const outcomeEndTime = performance.now();
        console.log(`ForecastVsActualsStep: Outcome data loaded in ${(outcomeEndTime - outcomeStartTime).toFixed(2)}ms`);
        const outcomeInfo = outcomeResponse || {};
        
        // Fetch staffing data (preferred source)
        let staffingResponse = {};
        try {
          console.log('ForecastVsActualsStep: Fetching staffing data...');
          const staffingStartTime = performance.now();
          const staffingDataResponse = await api.getStaffingData(outcomeId);
          const staffingEndTime = performance.now();
          console.log(`ForecastVsActualsStep: Staffing data loaded in ${(staffingEndTime - staffingStartTime).toFixed(2)}ms`);
          staffingResponse = staffingDataResponse.data || staffingDataResponse || {};
          
                    setStaffingData(staffingResponse);
        } catch (error) {
          console.log('ForecastVsActualsStep: No staffing data available');
          setStaffingData(null);
        }

                            // Fetch actual spend data and official forecast data if they exist
        let actualSpendData = {};
        let officialForecastData = {};
        try {
          console.log('ForecastVsActualsStep: Fetching spend data...');
          const spendStartTime = performance.now();
          const spendData = await api.getSpendData(outcomeId, year);
          actualSpendData = spendData || {};

          if (spendData.officialForecast) {
            officialForecastData = spendData.officialForecast;
          }

          if (process.env.NODE_ENV === 'development') {
            console.log(`📊 ForecastVsActualsStep: Spend data for outcome ${outcomeId}:`, {
              hasUploadedData: actualSpendData.hasUploadedData,
              monthlySpend: actualSpendData.monthlySpend,
              totalActual: actualSpendData.totalActual,
              companySpend: actualSpendData.companySpend,
              hasOfficialForecast: !!officialForecastData.monthlyForecast
            });
          }

          const spendEndTime = performance.now();
          console.log(`ForecastVsActualsStep: Spend data loaded in ${(spendEndTime - spendStartTime).toFixed(2)}ms`);
        } catch (error) {
          console.warn('ForecastVsActualsStep: No spend data available:', error);
        }
        
                        setOutcome({
          ...outcomeInfo,
          name: outcomeName || outcomeInfo.name || 'Unknown Outcome',
          actualSpendData,
          officialForecastData,
          companyOfficialForecast: officialForecastData.companyForecast || {}
        });
        
                setLoading(false);
      } catch (error) {
        console.error(`Error processing outcome ${outcomeId}:`, error);
        setError('Failed to load outcome data');
        setLoading(false);
      }
    };
    
        fetchOutcomeData();
  }, [outcomeId, outcomeName, year]);

  useEffect(() => {
    api.getDepartments().then(res => {
      const depts = res?.departments || (Array.isArray(res) ? res : []);
      setDepartments(depts);
    }).catch(() => {});
  }, []);
  
  

    

      // Upload functionality removed for wizard step

    

  const getSpendStatusIcon = (status) => {
    switch (status) {
      case 'on-track':        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'at-risk':         return <WarningAmberIcon fontSize="small" color="warning" />;
      case 'needs-attention': return <ReportProblemIcon fontSize="small" color="error" />;
      case 'off-track':       return <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.dark' }} />;
      default:                return <CheckCircleIcon fontSize="small" color="success" />;
    }
  };

  const getSpendStatusColor = (status) => {
    switch (status) {
      case 'on-track':        return 'success';
      case 'at-risk':         return 'warning';
      case 'needs-attention': return 'error';
      case 'off-track':       return 'error';
      default:                return 'success';
    }
  };

  const STATUS_DEFINITIONS = {
    'on-track':        'On Track — Projected forecast is within approved budget.',
    'at-risk':         'At Risk — YTD actuals exceed YTD projection, but full-year forecast is still within budget.',
    'needs-attention': 'Needs Attention — YTD actuals exceed YTD projection AND full-year forecast exceeds approved budget.',
    'off-track':       'Off Track — Projected forecast exceeds approved budget.',
  };

  // Excel export functionality
  const exportToExcel = useCallback(() => {
    if (!outcome || !staffingData) {
      alert('No data to export');
      return;
    }

    const exportData = [];
    const outcomeTotals = calculateOutcomeTotalsFromStaffing();
    const actualSpendData = getActualSpendData();
    
    // Create header row
    const headers = [
      'Category', 'Type', 'Company'
    ];
    
        // Add monthly columns
    MONTHS.forEach(month => {
      headers.push(month);
    });
    headers.push('Total');
    
    exportData.push(headers);

    // Helper function to add rows for a category
    const addCategoryRows = (categoryName, data, isActual = false) => {
      // Total row for category
      const totalRow = [
        categoryName,
        isActual ? 'Actual Total' : 'Forecast Total',
        'All Companies'
      ];
      
            if (isActual) {
        MONTHS.forEach((month, idx) => {
          const value = data.monthlyActual[idx];
          totalRow.push(value !== null ? value : 0);
        });
        totalRow.push(data.totalActualSpend);
      } else {
        MONTHS.forEach((month, idx) => {
          totalRow.push(data.monthlyProjected[idx]);
        });
        totalRow.push(data.totalProjected);
      }
      
      exportData.push(totalRow);
      
      // CAPEX row for category
      const capexRow = [
        categoryName,
        isActual ? 'Actual CAPEX' : 'Forecast CAPEX',
        'All Companies'
      ];
      
            if (isActual) {
        MONTHS.forEach((month, idx) => {
          const value = data.monthlyActualCapex[idx];
          capexRow.push(value !== null ? value : 0);
        });
        capexRow.push(data.totalActualCapex);
      } else {
        MONTHS.forEach((month, idx) => {
          capexRow.push(data.monthlyCapex[idx]);
        });
        capexRow.push(data.totalCapex);
      }
      
      exportData.push(capexRow);
      
      // OPEX row for category
      const opexRow = [
        categoryName,
        isActual ? 'Actual OPEX' : 'Forecast OPEX',
        'All Companies'
      ];
      
            if (isActual) {
        MONTHS.forEach((month, idx) => {
          const value = data.monthlyActualOpex[idx];
          opexRow.push(value !== null ? value : 0);
        });
        opexRow.push(data.totalActualOpex);
      } else {
        MONTHS.forEach((month, idx) => {
          opexRow.push(data.monthlyOpex[idx]);
        });
        opexRow.push(data.totalOpex);
      }
      
      exportData.push(opexRow);
      
      // Company breakdown rows
      const companies = Object.keys(isActual ? data.actualByCompany || {} : data.byCompany || {}).sort();
      companies.forEach(company => {
        const companyData = isActual ? data.actualByCompany[company] : data.byCompany[company];
        if (!companyData) return;
        
        // Company total
        const companyTotalRow = [
          categoryName,
          isActual ? 'Actual Total' : 'Forecast Total',
          company
        ];
        
                if (isActual) {
          MONTHS.forEach((month, idx) => {
            const value = companyData.monthlyActual[idx];
            companyTotalRow.push(value !== null ? value : 0);
          });
          companyTotalRow.push(companyData.totalActualSpend);
        } else {
          MONTHS.forEach((month, idx) => {
            companyTotalRow.push(companyData.monthlyProjected[idx]);
          });
          companyTotalRow.push(companyData.totalProjected);
        }
        
        exportData.push(companyTotalRow);
        
        // Company CAPEX
        const companyCapexRow = [
          categoryName,
          isActual ? 'Actual CAPEX' : 'Forecast CAPEX',
          company
        ];
        
                if (isActual) {
          MONTHS.forEach((month, idx) => {
            const value = companyData.monthlyActualCapex[idx];
            companyCapexRow.push(value !== null ? value : 0);
          });
          companyCapexRow.push(companyData.totalActualCapex);
        } else {
          MONTHS.forEach((month, idx) => {
            companyCapexRow.push(companyData.monthlyCapex[idx]);
          });
          companyCapexRow.push(companyData.totalCapex);
        }
        
        exportData.push(companyCapexRow);
        
        // Company OPEX
        const companyOpexRow = [
          categoryName,
          isActual ? 'Actual OPEX' : 'Forecast OPEX',
          company
        ];
        
                if (isActual) {
          MONTHS.forEach((month, idx) => {
            const value = companyData.monthlyActualOpex[idx];
            companyOpexRow.push(value !== null ? value : 0);
          });
          companyOpexRow.push(companyData.totalActualOpex);
        } else {
          MONTHS.forEach((month, idx) => {
            companyOpexRow.push(companyData.monthlyOpex[idx]);
          });
          companyOpexRow.push(companyData.totalOpex);
        }
        
        exportData.push(companyOpexRow);
      });
    };

    // Add forecast data
    addCategoryRows('FORECAST', outcomeTotals, false);
    
    // Add spacing row
    exportData.push([]);
    
    // Add actual data
    addCategoryRows('ACTUAL', actualSpendData, true);
    
    // Add spacing row
    exportData.push([]);
    
    // Add variance data
    const varianceRow = [
      'VARIANCE',
      'Total Variance',
      'All Companies'
    ];
    
        MONTHS.forEach((month, idx) => {
      const actual = actualSpendData.monthlyActual[idx];
      const projected = outcomeTotals.monthlyProjected[idx];
      const variance = actual !== null ? actual - projected : null;
      varianceRow.push(variance !== null ? variance : 0);
    });
    
    const totalVariance = actualSpendData.totalActualSpend - outcomeTotals.totalProjected;
    varianceRow.push(totalVariance);
    exportData.push(varianceRow);
    
        // Simplified export without complex company breakdown

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(exportData);
    
    // Set column widths
    const colWidths = [
      { wch: 15 }, // Category
      { wch: 20 }, // Type
      { wch: 15 }, // Company
    ];
    
        // Add monthly column widths
    MONTHS.forEach(() => {
      colWidths.push({ wch: 12 });
    });
    colWidths.push({ wch: 15 }); // Total
    
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Forecast vs Actuals');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const outcomeName = outcome.name ? outcome.name.replace(/[^a-zA-Z0-9]/g, '_') : 'Outcome';
    const filename = `${outcomeName}_Forecast_vs_Actuals_${year}_${timestamp}.xlsx`;
    
    // Save file
    XLSX.writeFile(wb, filename);
    
        console.log(`Exported forecast vs actuals data to ${filename}`);
  }, [outcome, staffingData, calculateOutcomeTotalsFromStaffing, getActualSpendData, MONTHS, year]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography color="text.secondary">Loading forecast vs actuals data...</Typography>
      </Box>
    );
  }

  if (!outcome) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load outcome data for forecast vs actuals comparison.
      </Alert>
    );
  }

  // Dept filter: pre-filter staffing roles to the selected department
  const effectiveStaffingData = selectedDeptFilter && staffingData ? {
    ...staffingData,
    milestones: (staffingData.milestones || []).map(m => ({
      ...m,
      roles: (m.roles || []).filter(r => !r.department || r.department === selectedDeptFilter)
    }))
  } : null;

  const outcomeTotals = calculateOutcomeTotalsFromStaffing(effectiveStaffingData);

  // Unfiltered totals needed to compute "% of total" when dept filter is active
  const unfilteredTotals = (!selectedDeptFilter || !staffingData) ? null
    : calculateOutcomeTotalsFromStaffing(null);

  // 4-metric dept card: projected, approved, remaining, % of total
  const deptCardMetrics = (() => {
    if (!selectedDeptFilter) return null;
    const deptBudget = (outcome?.department_budgets || []).find(
      db => db.department_name === selectedDeptFilter
    );
    const deptApproved = deptBudget ? (deptBudget.capex + deptBudget.opex) : 0;
    const deptProjected = Object.values(outcomeTotals?.companyBreakdown || {})
      .reduce((s, c) => s + (c.total || 0), 0);
    const totalProjected = unfilteredTotals
      ? Object.values(unfilteredTotals.companyBreakdown || {}).reduce((s, c) => s + (c.total || 0), 0)
      : deptProjected;
    const remaining = deptApproved - deptProjected;
    const pctOfTotal = totalProjected > 0 ? (deptProjected / totalProjected) * 100 : 0;
    return { deptApproved, deptProjected, remaining, pctOfTotal };
  })();
  const actualSpendData = getActualSpendData();
  const officialForecastData = getOfficialForecastData();
  const approvedBudget = (outcome?.approved_budget?.capex || 0) + (outcome?.approved_budget?.opex || 0);
  const overallSpendStatus = calculateSpendStatus(
    actualSpendData.totalActualSpend,
    officialForecastData.hasOfficialForecast ? officialForecastData.totalOfficialForecast : outcomeTotals.totalProjected,
    actualSpendData.monthlyActual,
    officialForecastData.hasOfficialForecast ? officialForecastData.monthlyOfficialForecast : outcomeTotals.monthlyProjected,
    approvedBudget,
    outcomeTotals.totalProjected
  );
  
  // Create outcome object with company breakdown for rendering
  const outcomeForRendering = {
    companyBreakdown: outcomeTotals.companyBreakdown,
    companyActuals: outcomeTotals.companyActuals,
    monthlyProjected: outcomeTotals.monthlyProjected,
    monthlyActual: actualSpendData.monthlyActual
  };

  // EOY Projection: actuals through cutoff, projected for remainder
  const latestActualMonth = getLatestActualSpendMonth(actualSpendData.monthlyActual);
  const eoyMonthly = outcomeTotals.monthlyProjected.map((proj, i) =>
    i <= latestActualMonth && latestActualMonth >= 0
      ? (actualSpendData.monthlyActual[i] || 0)
      : proj
  );
  const eoyTotal = latestActualMonth >= 0
    ? eoyMonthly.reduce((s, v) => s + (v || 0), 0)
    : 0;

  // PPM EOY: same blend using pure staffing instead of official forecast
  const ppmMonthlyStaffing = outcomeTotals.monthlyStaffingProjected || outcomeTotals.monthlyProjected;
  const ppmEoyMonthly = ppmMonthlyStaffing.map((ppmProj, i) =>
    i <= latestActualMonth && latestActualMonth >= 0
      ? (actualSpendData.monthlyActual[i] || 0)
      : ppmProj
  );
  const ppmEoyTotal = latestActualMonth >= 0
    ? ppmEoyMonthly.reduce((s, v) => s + (v || 0), 0)
    : 0;

  // Extract actual CAPEX/OPEX for tooltip use
  let actualCapex = outcome?.actualSpendData?.totalActualCapex || 0;
  let actualOpex  = outcome?.actualSpendData?.totalActualOpex  || 0;
  if (actualCapex === 0 && actualOpex === 0 && actualSpendData.totalActualSpend > 0) {
    if (outcomeTotals.companyActuals && Object.keys(outcomeTotals.companyActuals).length > 0) {
      actualCapex = Object.values(outcomeTotals.companyActuals).reduce((s, c) => s + (c.capex || 0), 0);
      actualOpex  = Object.values(outcomeTotals.companyActuals).reduce((s, c) => s + (c.opex  || 0), 0);
    }
  }

  // EOY CAPEX/OPEX blend: actual C/O through cutoff + projected C/O for remainder months
  const eoyCapex = latestActualMonth >= 0
    ? actualCapex + outcomeTotals.monthlyCapex.slice(latestActualMonth + 1).reduce((s, v) => s + (v || 0), 0)
    : 0;
  const eoyOpex = latestActualMonth >= 0
    ? actualOpex + outcomeTotals.monthlyOpex.slice(latestActualMonth + 1).reduce((s, v) => s + (v || 0), 0)
    : 0;

  // Tooltip helper — matches MonthlyTooltipContent pattern from main FVA portfolio view
  const MonthlyTooltipContent = ({ title, capex, opex, monthly, limitToMonth }) => {
    const total = (capex || 0) + (opex || 0);
    const limit = limitToMonth != null ? limitToMonth + 1 : 12;
    return (
      <Box sx={{ minWidth: 200, py: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>{title}</Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.25, opacity: 0.85 }}>
          Total: {formatCurrencyFull(total)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.85 }}>
          C: {formatCurrencyFull(capex || 0)} | O: {formatCurrencyFull(opex || 0)}
        </Typography>
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
          {MONTHS.slice(0, limit).map((m, i) => (
            <Box key={m} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>{m}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrencyFull((monthly && monthly[i]) || 0)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
            {/* Upload Dialog removed for wizard step */}

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">
          Step 5 of 5: Forecast vs Actuals
        </Typography>
                {outcome.name && (
          <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
            {outcome.name}
          </Typography>
        )}
                
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Department filter chips */}
      {(() => {
        const outcomeDeptNames = (outcome?.department_budgets || []).map(db => db.department_name);
        const chipDepts = outcomeDeptNames.length > 0
          ? departments.filter(d => outcomeDeptNames.includes(d.name))
          : departments;
        if (chipDepts.length === 0) return null;
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Filter by dept:</Typography>
            <Chip label="All" size="small"
              onClick={() => setSelectedDeptFilter(null)}
              color={!selectedDeptFilter ? 'primary' : 'default'}
              variant={!selectedDeptFilter ? 'filled' : 'outlined'} />
            {chipDepts.map(d => (
              <Chip key={d.id} label={d.name} size="small"
                onClick={() => setSelectedDeptFilter(selectedDeptFilter === d.name ? null : d.name)}
                color={selectedDeptFilter === d.name ? 'warning' : 'default'}
                variant={selectedDeptFilter === d.name ? 'filled' : 'outlined'} />
            ))}
          </Box>
        );
      })()}

      {/* Dept budget banner */}
      {selectedDeptFilter && deptCardMetrics && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {selectedDeptFilter} Department
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Projected</Typography>
              <Typography variant="body2" fontWeight={600}>{formatCurrency(deptCardMetrics.deptProjected)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Approved</Typography>
              <Typography variant="body2" fontWeight={600}>{formatCurrency(deptCardMetrics.deptApproved)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
              <Typography variant="body2" fontWeight={600}
                color={deptCardMetrics.remaining >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(Math.abs(deptCardMetrics.remaining))} {deptCardMetrics.remaining >= 0 ? '✓' : '↑ over'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">% of Total</Typography>
              <Typography variant="body2" fontWeight={600}>{deptCardMetrics.pctOfTotal.toFixed(1)}%</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Outcome-Level Summary */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Outcome Budget Summary</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={exportToExcel}
                color="primary"
                size="small"
                disabled={!outcome || !staffingData}
              >
                Export to Excel
              </Button>
                            {/* Upload button removed for wizard step */}
            </Box>
          </Box>

                                        {/* Outcome Overview Cards */}
          {(() => {
            const ytdActual = latestActualMonth >= 0
              ? (actualSpendData.monthlyActual || []).slice(0, latestActualMonth + 1).reduce((s, v) => s + (v || 0), 0)
              : 0;
            const ytdPpm = latestActualMonth >= 0
              ? ppmMonthlyStaffing.slice(0, latestActualMonth + 1).reduce((s, v) => s + (v || 0), 0)
              : 0;
            const ytdVsPpm = ytdActual - ytdPpm;
            const ytdVsPpmIsOver = ytdVsPpm > 0;
            const eoyVsPpm = eoyTotal - outcomeTotals.totalProjected;
            const eoyVsPpmIsOver = eoyVsPpm > 0;
            const eoyVsBudget = eoyTotal - approvedBudget;
            const eoyVsBudgetIsOver = eoyVsBudget > 0;
            return (
        <Grid container spacing={2} alignItems="stretch" sx={{ mb: 3 }}>
            {/* Approved Budget */}
            <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
              <Tooltip arrow title={
                <Box sx={{ minWidth: 200, py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Approved Budget</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.25, opacity: 0.85 }}>
                    Total: {formatCurrencyFull(approvedBudget)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                    C: {formatCurrencyFull(outcome.approved_budget?.capex || 0)} | O: {formatCurrencyFull(outcome.approved_budget?.opex || 0)}
                  </Typography>
                </Box>
              }>
                <Card variant="outlined" sx={{ p: 2, bgcolor: alpha('#2196f3', 0.1), height: '100%', width: '100%', display: 'flex', flexDirection: 'column', cursor: 'help' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Approved Budget</Typography>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="h6" color="primary.main" fontWeight={600}>
                      {formatCurrency(approvedBudget)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      C: {formatCurrency(outcome.approved_budget?.capex || 0)} | O: {formatCurrency(outcome.approved_budget?.opex || 0)}
                    </Typography>
                  </Box>
                </Card>
              </Tooltip>
            </Grid>

            {/* PPM Projection */}
            <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
              <Tooltip arrow title={
                <Box sx={{ minWidth: 200, py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>PPM Projection</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.25, opacity: 0.85 }}>
                    Total: {formatCurrencyFull(outcomeTotals.totalProjected)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.85 }}>
                    C: {formatCurrencyFull(outcomeTotals.totalCapex)} | O: {formatCurrencyFull(outcomeTotals.totalOpex)}
                  </Typography>
                  <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
                    {MONTHS.map((m, i) => (
                      <Box key={m} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{m}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {formatCurrencyFull(ppmMonthlyStaffing[i] || 0)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              }>
                <Card variant="outlined" sx={{ p: 2, bgcolor: alpha('#4caf50', 0.1), height: '100%', width: '100%', display: 'flex', flexDirection: 'column', cursor: 'help' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>PPM Projection</Typography>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="h6" color="success.main" fontWeight={600}>
                      {formatCurrency(outcomeTotals.totalProjected)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      C: {formatCurrency(outcomeTotals.totalCapex)} | O: {formatCurrency(outcomeTotals.totalOpex)}
                    </Typography>
                  </Box>
                </Card>
              </Tooltip>
            </Grid>

            {/* YTD Actuals */}
            <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
              <Tooltip arrow title={<MonthlyTooltipContent title="YTD Actuals" capex={actualCapex} opex={actualOpex} monthly={actualSpendData.monthlyActual} limitToMonth={latestActualMonth} />}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: alpha('#ff9800', 0.1), height: '100%', width: '100%', display: 'flex', flexDirection: 'column', cursor: 'help' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>YTD Actuals</Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h6" color="warning.main" fontWeight={600}>
                    {formatCurrency(ytdActual)}
                  </Typography>
                  {ytdPpm > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.3, color: ytdVsPpmIsOver ? 'error.main' : 'success.main' }}>
                      PPM: {formatCurrency(ytdPpm)} {ytdVsPpmIsOver ? '↑' : '↓'} {formatCurrency(Math.abs(ytdVsPpm))} {ytdVsPpmIsOver ? 'over' : 'under'}
                    </Typography>
                  )}
                </Box>
              </Card>
              </Tooltip>
            </Grid>

            {/* EOY Projection */}
            <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
              <Tooltip arrow title={<MonthlyTooltipContent title="EOY Projection" capex={eoyCapex} opex={eoyOpex} monthly={eoyMonthly} />}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: alpha('#0288d1', 0.1), height: '100%', width: '100%', display: 'flex', flexDirection: 'column', cursor: 'help' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>EOY Projection</Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h6" color="info.main" fontWeight={600}>
                    {formatCurrency(eoyTotal)}
                  </Typography>
                  {outcomeTotals.totalProjected > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.3, color: eoyVsPpmIsOver ? 'error.main' : 'success.main' }}>
                      {eoyVsPpmIsOver ? '↑' : '↓'} {formatCurrency(Math.abs(eoyVsPpm))} vs PPM
                    </Typography>
                  )}
                </Box>
              </Card>
              </Tooltip>
            </Grid>

            {/* vs Approved Budget */}
            <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
              <Tooltip arrow title={
                <Box sx={{ minWidth: 200, py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>EOY vs Approved Budget</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.25, opacity: 0.85 }}>EOY Projection: {formatCurrencyFull(eoyTotal)}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>Approved Budget: {formatCurrencyFull(approvedBudget)}</Typography>
                </Box>
              }>
              <Card variant="outlined" sx={{ p: 2, bgcolor: eoyVsBudgetIsOver ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1), height: '100%', width: '100%', display: 'flex', flexDirection: 'column', cursor: 'help' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>vs Approved Budget</Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h6" sx={{ color: eoyVsBudgetIsOver ? 'error.main' : 'success.main' }} fontWeight={600}>
                    {eoyVsBudget >= 0 ? '+' : ''}{formatCurrency(eoyVsBudget)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {eoyVsBudgetIsOver ? 'over budget' : 'under budget'}
                  </Typography>
                </Box>
              </Card>
              </Tooltip>
            </Grid>
          </Grid>
            );
          })()}

        </CardContent>
      </Card>

                  {/* Monthly Breakdown Table — FVA-parity layout */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly Breakdown — Company Detail
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', width: 280, minWidth: 280 }}>
                    Row
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: 90, minWidth: 90 }}>Total</TableCell>
                  {MONTHS.map(m => (
                    <TableCell key={m} align="right" sx={{ fontWeight: 700, width: 58, minWidth: 58, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{m}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const chipSz = { fontSize: '0.65rem', height: 18 };
                  const subRowSx = (color) => ({ bgcolor: `${color}.50` });
                  const subLabelSx = { pl: 3, fontSize: '0.75rem', color: 'text.secondary' };
                  const rowBorder = { borderTop: '2px solid', borderTopColor: 'divider' };
                  const rows = [];

                  const pushRow = (rowKey, badgeChip, total, monthly, rowSx = {}, isFirst = false, hasCapexRows = false, textColor = null) => {
                    if (total === 0 && !monthly.some(v => (v || 0) !== 0)) return;
                    const effectiveSx = { ...rowSx, ...(isFirst ? { ...rowBorder, bgcolor: 'action.hover' } : {}) };
                    rows.push(
                      <TableRow key={rowKey} sx={effectiveSx}>
                        <TableCell sx={{ fontSize: '0.8rem', position: 'sticky', left: 0, zIndex: 1, bgcolor: isFirst ? 'action.hover' : 'background.paper' }}>
                          <Box display="flex" alignItems="center" width="100%" gap={1}>
                            {badgeChip}
                            {hasCapexRows && (
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleCapex(rowKey); }}
                                sx={{ ml: 'auto', p: 0.25, flexShrink: 0 }}>
                                {expandedCapex.has(rowKey) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', ...(textColor ? { color: textColor } : {}) }}>
                          <Tooltip title={formatCurrencyFull(total)} placement="left" disableHoverListener={Math.abs(total) < 1_000_000}>
                            <span>{formatCurrency(total)}</span>
                          </Tooltip>
                        </TableCell>
                        {MONTHS.map((_, i) => (
                          <TableCell key={i} align="right" sx={{ fontSize: '0.68rem', px: 0.5, ...(textColor ? { color: textColor } : {}) }}>
                            {(monthly[i] || 0) !== 0
                              ? <Tooltip title={formatCurrencyFull(monthly[i])} placement="top" disableHoverListener={Math.abs(monthly[i] || 0) < 1_000_000}><span>{formatCurrency(monthly[i])}</span></Tooltip>
                              : '—'}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  };

                  const pushSubRows = (rowKey, color, capexVal, opexVal, capexMonthly, opexMonthly) => {
                    if (capexVal === 0 && opexVal === 0) return;
                    if (!expandedCapex.has(rowKey)) return;
                    if (capexVal !== 0) rows.push(
                      <TableRow key={`${rowKey}-CAPEX`} sx={subRowSx(color)}>
                        <TableCell sx={{ ...subLabelSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${color}.50` }}><Chip label="CAPEX" size="small" color={color} variant="outlined" sx={chipSz} /></TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(capexVal)} placement="left" disableHoverListener={Math.abs(capexVal) < 1_000_000}><span>{formatCurrency(capexVal)}</span></Tooltip></TableCell>
                        {MONTHS.map((_, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.68rem', px: 0.5 }}>{(capexMonthly[i] || 0) !== 0 ? <Tooltip title={formatCurrencyFull(capexMonthly[i])} placement="top" disableHoverListener={Math.abs(capexMonthly[i] || 0) < 1_000_000}><span>{formatCurrency(capexMonthly[i])}</span></Tooltip> : '—'}</TableCell>)}
                      </TableRow>
                    );
                    if (opexVal !== 0) rows.push(
                      <TableRow key={`${rowKey}-OPEX`} sx={subRowSx(color)}>
                        <TableCell sx={{ ...subLabelSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${color}.50` }}><Chip label="OPEX" size="small" color={color} variant="outlined" sx={chipSz} /></TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(opexVal)} placement="left" disableHoverListener={Math.abs(opexVal) < 1_000_000}><span>{formatCurrency(opexVal)}</span></Tooltip></TableCell>
                        {MONTHS.map((_, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.68rem', px: 0.5 }}>{(opexMonthly[i] || 0) !== 0 ? <Tooltip title={formatCurrencyFull(opexMonthly[i])} placement="top" disableHoverListener={Math.abs(opexMonthly[i] || 0) < 1_000_000}><span>{formatCurrency(opexMonthly[i])}</span></Tooltip> : '—'}</TableCell>)}
                      </TableRow>
                    );
                  };

                  // ── Outcome-level summary rows ──────────────────────────────────

                  // Approved Budget
                  const approvedTotal = (outcome.approved_budget?.capex || 0) + (outcome.approved_budget?.opex || 0);
                  if (approvedTotal > 0) {
                    rows.push(
                      <TableRow key="approved" sx={{ bgcolor: alpha('#2196f3', 0.08), borderTop: '2px solid', borderTopColor: 'primary.main' }}>
                        <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: alpha('#2196f3', 0.08) }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">Approved Budget</Typography>
                            <Chip size="small" label="Approved" color="primary" variant="outlined" sx={chipSz} />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={formatCurrencyFull(approvedTotal)} placement="left" disableHoverListener={Math.abs(approvedTotal) < 1_000_000}>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">{formatCurrency(approvedTotal)}</Typography>
                          </Tooltip>
                        </TableCell>
                        {MONTHS.map((_, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</TableCell>)}
                      </TableRow>
                    );
                  }

                  // EOY Projection — only when actuals exist
                  if (latestActualMonth >= 0) {
                    rows.push(
                      <TableRow key="eoy" sx={{ backgroundColor: alpha('#0288d1', 0.06) }}>
                        <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: alpha('#0288d1', 0.06) }}>
                          <Tooltip arrow placement="right" title="Full-year landing estimate: actuals through the latest uploaded month, projected for remaining months.">
                            <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'help', width: 'fit-content' }}>
                              <Typography variant="body2" fontWeight="bold" color="info.main">EOY Projection</Typography>
                              <Chip size="small" label="EOY" color="info" variant="outlined" sx={chipSz} />
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={formatCurrencyFull(eoyTotal)} placement="left" disableHoverListener={Math.abs(eoyTotal) < 1_000_000}>
                            <Typography variant="body2" fontWeight="bold" color="info.main">{formatCurrency(eoyTotal)}</Typography>
                          </Tooltip>
                        </TableCell>
                        {eoyMonthly.map((amount, i) => (
                          <TableCell key={i} align="right" sx={{ fontSize: '0.75rem', color: 'info.main' }}>
                            {amount > 0
                              ? <Tooltip title={formatCurrencyFull(amount)} placement="top" disableHoverListener={Math.abs(amount) < 1_000_000}><span>{formatCurrency(amount)}</span></Tooltip>
                              : '—'}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  }

                  // Projected Forecast (outcome total)
                  pushRow('outcome-proj',
                    <Tooltip arrow title="PPM Staffing Model projection — includes fixed costs.">
                      <Chip label="Projected Forecast" size="small" color="success" variant="outlined" sx={chipSz} />
                    </Tooltip>,
                    outcomeTotals.totalProjected, outcomeTotals.monthlyProjected, {}, false, false, 'success.main');

                  // Official Forecast (outcome total)
                  if (officialForecastData.hasOfficialForecast) {
                    pushRow('outcome-official',
                      <Chip label="Official Forecast" size="small" color="secondary" variant="outlined" sx={chipSz} />,
                      officialForecastData.totalOfficialForecast, officialForecastData.monthlyOfficialForecast, {}, false, false, 'secondary.main');
                  }

                  // Actual Spend (outcome total)
                  if (actualSpendData.totalActualSpend > 0) {
                    pushRow('outcome-actual',
                      <Chip label="Actual Spend" size="small" color="warning" variant="outlined" sx={chipSz} />,
                      actualSpendData.totalActualSpend, actualSpendData.monthlyActual, {}, false, false, 'warning.main');
                  }

                  // ── Per-company rows ────────────────────────────────────────────
                  const allCompanies = new Set([
                    ...Object.keys(outcomeTotals.companyBreakdown || {}),
                    ...Object.keys(outcomeTotals.companyActuals || {}),
                    ...Object.keys(outcomeTotals.companyStaffingBreakdown || {}),
                    ...Object.keys(outcome?.companyOfficialForecast || {}),
                  ]);
                  if (outcomeTotals.fixedCostBreakdown) {
                    Object.values(outcomeTotals.fixedCostBreakdown).forEach(fc => allCompanies.add(fc.company || 'Other'));
                  }

                  Array.from(allCompanies).sort().forEach((company) => {
                    const k = company;
                    const fullBreakdown = outcomeTotals.companyBreakdown?.[company] || { total: 0, capex: 0, opex: 0, monthlyForecast: [], monthlyCapex: [], monthlyOpex: [] };
                    const staffingSnap = outcomeTotals.companyStaffingBreakdown?.[company] || null;
                    const actual = outcomeTotals.companyActuals?.[company] || { total: 0, capex: 0, opex: 0 };
                    const official = outcome?.companyOfficialForecast?.[company] || { total: 0 };
                    const monthlyActualDist = outcomeTotals.companyMonthlyActuals?.[company] || Array(12).fill(0);
                    const monthlyProjected = fullBreakdown.monthlyForecast || Array(12).fill(0);
                    const monthlyOfficial = official.monthlyForecast || Array(12).fill(0);
                    const projCapexMonthly = fullBreakdown.monthlyCapex?.length ? fullBreakdown.monthlyCapex : Array(12).fill(0);
                    const projOpexMonthly = fullBreakdown.monthlyOpex?.length ? fullBreakdown.monthlyOpex : Array(12).fill(0);
                    const companyFixedCosts = Object.entries(outcomeTotals.fixedCostBreakdown || {})
                      .filter(([, fc]) => (fc.company || 'Other') === company);

                    const companyLabel = (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={600}>{company}</Typography>
                      </Box>
                    );

                    // isFirst=true triggers company label + border styling via pushRow
                    const projLabel = staffingSnap ? 'Staffing Forecast' : 'Projected';
                    const hasCX = fullBreakdown.capex !== 0 || fullBreakdown.opex !== 0;

                    // Projected row (first row for company — shows company label)
                    const projRowSx = {};
                    const projKey = `${k}-proj`;
                    if (fullBreakdown.total !== 0 || monthlyProjected.some(v => v !== 0)) {
                      rows.push(
                        <TableRow key={projKey} sx={{ ...rowBorder, bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontSize: '0.8rem', position: 'sticky', left: 0, zIndex: 1, bgcolor: 'action.hover' }}>
                            <Box display="flex" alignItems="center" width="100%" gap={1}>
                              {companyLabel}
                              <Chip label={projLabel} size="small" color="success" variant="outlined" sx={chipSz} />
                              {hasCX && (
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleCapex(projKey); }}
                                  sx={{ ml: 'auto', p: 0.25, flexShrink: 0 }}>
                                  {expandedCapex.has(projKey) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'success.main' }}>
                            <Tooltip title={formatCurrencyFull(fullBreakdown.total)} placement="left" disableHoverListener={Math.abs(fullBreakdown.total) < 1_000_000}>
                              <span>{formatCurrency(fullBreakdown.total)}</span>
                            </Tooltip>
                          </TableCell>
                          {MONTHS.map((_, i) => (
                            <TableCell key={i} align="right" sx={{ fontSize: '0.75rem', color: 'success.main' }}>
                              {(monthlyProjected[i] || 0) !== 0
                                ? <Tooltip title={formatCurrencyFull(monthlyProjected[i])} placement="top" disableHoverListener={Math.abs(monthlyProjected[i] || 0) < 1_000_000}><span>{formatCurrency(monthlyProjected[i])}</span></Tooltip>
                                : '—'}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                      pushSubRows(projKey, 'success', fullBreakdown.capex, fullBreakdown.opex, projCapexMonthly, projOpexMonthly);
                    }

                    // Fixed costs sub-rows for this company
                    companyFixedCosts.forEach(([costName, fc]) => {
                      const fcMonthly = fc.monthlyForecast || Array(12).fill(0);
                      pushRow(`${k}-fixed-${costName}`,
                        <Tooltip arrow title={fc.details || costName}>
                          <Chip label={`Fixed: ${costName}`} size="small" color="info" variant="outlined" sx={{ ...chipSz, maxWidth: 160 }} />
                        </Tooltip>,
                        fc.total, fcMonthly, {});
                    });

                    // Official Forecast row
                    if (official.total > 0) {
                      pushRow(`${k}-official`,
                        <Chip label="Official Forecast" size="small" color="secondary" variant="outlined" sx={chipSz} />,
                        official.total, monthlyOfficial, {}, false, false, 'secondary.main');
                    }

                    // Actual Spend row
                    if (actual.total > 0 || monthlyActualDist.some(v => v > 0)) {
                      const actualCapexMonthly = outcomeTotals.companyActuals?.[company]?.monthlyCapex || Array(12).fill(0);
                      const actualOpexMonthly = outcomeTotals.companyActuals?.[company]?.monthlyOpex || Array(12).fill(0);
                      const actKey = `${k}-actual`;
                      pushRow(actKey,
                        <Chip label="Actual Spend" size="small" color="warning" variant="outlined" sx={chipSz} />,
                        actual.total, monthlyActualDist, {}, false,
                        actual.capex !== 0 || actual.opex !== 0, 'warning.main');
                      pushSubRows(actKey, 'warning', actual.capex, actual.opex, actualCapexMonthly, actualOpexMonthly);

                      // Variance: actual − projected
                      const varianceMonthly = Array(12).fill(0).map((_, i) => (monthlyActualDist[i] || 0) - (monthlyProjected[i] || 0));
                      const varianceTotal = actual.total - fullBreakdown.total;
                      const varianceCapex = (actual.capex || 0) - fullBreakdown.capex;
                      const varianceOpex  = (actual.opex  || 0) - fullBreakdown.opex;
                      const varianceCapexMonthly = Array(12).fill(0);
                      const varianceOpexMonthly  = Array(12).fill(0);
                      const varKey = `${k}-variance`;
                      const varChipColor = varianceTotal > 0 ? 'error' : 'success';
                      pushRow(varKey,
                        <Chip label="Variance" size="small" color={varChipColor} variant="outlined" sx={chipSz} />,
                        varianceTotal, varianceMonthly, {}, false,
                        varianceCapex !== 0 || varianceOpex !== 0,
                        varianceTotal > 0 ? 'error.main' : 'success.main');
                      pushSubRows(varKey, varChipColor, varianceCapex, varianceOpex, varianceCapexMonthly, varianceOpexMonthly);
                    }
                  });

                  return rows;
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Buttons - only show if not hideButtons */}
      {!hideButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => window.history.back()}
            sx={secondaryButtonStyle}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            sx={buttonStyle}
            disabled={loading}
          >
            Complete Outcome
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default ForecastVsActualsStep;
                                
                              